package com.oasisfeng.invaluable.social

/**
 * A debouncing scheduler for [SocialClient]'s `coalesce` seam.
 *
 * A late joiner's catch-up applies a whole chain suffix as a burst of back-to-back peer events; reacting to every one
 * of them separately is O(events²) work over a growing store. This collapses such a burst into a single flush [quietMs]
 * after the last advance — long enough that a burst always settles first, short enough to be imperceptible for a lone
 * live event. Both the browser and Node expose the global `setTimeout`/`clearTimeout` used here.
 *
 * Right for work that is *cheap and wanted promptly* — bumping the change version, re-rendering. For persistence, whose
 * cost scales with the whole store, see [costAwareCoalescer]: waiting for silence is the wrong trigger when the flush
 * itself is what creates the silence.
 */
fun debouncingCoalescer(quietMs: Int = 30): (flush: () -> Unit) -> Unit {
    var timer: dynamic = null
    return { flush ->
        if (timer != null) js("clearTimeout")(timer)
        timer = js("setTimeout")(fun() { timer = null; flush() }, quietMs)
    }
}

/**
 * A coalescer that bounds the share of time a flush may consume, for a flush too expensive to trigger on silence.
 *
 * [debouncingCoalescer] assumes a burst settles while nothing else is happening. Persisting breaks that assumption by
 * being *itself* the thing that happens: a full-state write of a multi-megabyte store blocks the single JS thread for
 * seconds, inbound frames pile up in the socket while it runs, and when it returns the runtime drains them and then
 * falls silent again — not because the peer stopped sending, but because the flush stopped listening. That manufactured
 * silence is what re-arms the debounce. Flush count is then set by how long a flush blocks rather than by how much
 * changed, which is backwards in the worst way: making a flush cheaper shortens the silence it manufactures and buys
 * *more* flushes, returning only a fraction of the saving (measured — see the catch-up attribution in `dog-fooding.md`).
 *
 * So this schedules on a different rule. The first change after an idle period arms a flush [quietMs] later; further
 * changes join that same pending flush rather than pushing it back, so nothing is starved however long the traffic
 * lasts. After each flush, the next one is held off for [budgetShare] times what that flush just cost, measured. Two
 * properties follow: a flush never occupies more than `1 / (1 + budgetShare)` of the time, whatever the store grows to,
 * and a change lands at most one cooldown after it arrives. Both are stated in terms the caller can check; neither
 * depends on the traffic falling silent.
 *
 * Deferring a *remote* advance's write is safe in the way that matters: replication is grow-only, so anything not yet
 * written is simply re-synced from a peer on the next boot. Local authoring does not come through here — it persists
 * synchronously in `SocialClient.perform`.
 *
 * [nowMillis] and [schedule] are seams so the policy can be tested on a controlled clock instead of a real one.
 */
fun costAwareCoalescer(
    quietMs: Int = 30,
    budgetShare: Int = 4,
    nowMillis: () -> Double = { js("Date.now()") as Double },
    schedule: (delayMs: Double, run: () -> Unit) -> Unit = { delayMs, run -> js("setTimeout")(run, delayMs) },
): (flush: () -> Unit) -> Unit {
    var pending = false
    var readyAt = 0.0   // no flush before this instant: the cooldown owed for the previous one
    return { flush ->
        if (! pending) {
            pending = true
            schedule(maxOf(quietMs.toDouble(), readyAt - nowMillis())) {
                pending = false
                val startedAt = nowMillis()
                try { flush() } finally { readyAt = nowMillis() + (nowMillis() - startedAt) * budgetShare }
            }
        }
    }
}
